/**
 * Pick the PlayEngine3D games a pe3d base offers.
 *
 * The third of the checklists (SelectorBases, SelectorCompanies) and the same
 * control to look at: their chrome from settingsPalette, a search, a count,
 * All / None. What it adds is columns — a game is told apart by its package
 * and how far its imports have got, so those line up down the list.
 *
 * @param props.games     [{ package, title, latest, releases }] — every imported game
 * @param props.selected  package names on the base
 * @param props.onChange  (packages) => void
 */

import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { MUTED, SEARCH_INPUT_PROPS, useChecklistStyles } from "./settingsPalette";

const COLUMNS = "34px minmax(0, 1.1fr) minmax(0, 1.4fr) 110px";

const useStyles = makeStyles(() => ({
  header: { display: "grid", gridTemplateColumns: COLUMNS, alignItems: "center", columnGap: "8px", paddingLeft: "8px", paddingRight: "8px" },
  row: { display: "grid", gridTemplateColumns: COLUMNS, alignItems: "center", columnGap: "8px" },
  cell: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  muted: { fontSize: "0.72rem", color: MUTED },
  latest: { fontSize: "0.72rem", color: MUTED, fontVariantNumeric: "tabular-nums" },
}));

export default function SelectorPe3dGames(props) {
  const classes = useChecklistStyles();
  const local = useStyles();
  const [query, setQuery] = useState("");

  const selected = useMemo(() => new Set((props.selected || []).map(String)), [props.selected]);
  const available = props.games || [];

  const matching = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return available;
    return available.filter((game) => (game.title || "").toLowerCase().includes(needle) || game.package.toLowerCase().includes(needle));
  }, [available, query]);

  const setSelection = (nextSet) => props.onChange(Array.from(nextSet));

  const toggle = (name) => {
    const next = new Set(selected);
    if (next.has(name)) {
      next.delete(name);
    } else {
      next.add(name);
    }
    setSelection(next);
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.toolbar}>
        <TextField
          className={classes.search}
          size="small"
          placeholder="Search games"
          InputProps={SEARCH_INPUT_PROPS}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <span className={classes.count}>
          {selected.size} / {available.length}
        </span>
        <Button className={classes.linkButton} onClick={() => setSelection(new Set(available.map((game) => game.package)))}>
          All
        </Button>
        <Button className={classes.linkButton} onClick={() => setSelection(new Set())}>
          None
        </Button>
      </div>

      <div className={`${classes.scroller} ${classes.scrollerCapped}`}>
        {matching.length > 0 && (
          <div className={`${classes.groupHeader} ${local.header}`}>
            <span />
            <span className={classes.groupTitle}>Game</span>
            <span className={classes.groupTitle}>Package</span>
            <span className={classes.groupTitle}>Imported</span>
          </div>
        )}
        {matching.length === 0 && <div className={classes.empty}>{props.emptyText || "No games to show."}</div>}
        {matching.map((game) => (
          <div key={game.package} className={`${classes.row} ${local.row}`} onClick={() => toggle(game.package)}>
            <Checkbox size="small" checked={selected.has(game.package)} onChange={() => toggle(game.package)} />
            <span className={`${classes.rowLabel} ${local.cell}`}>{game.title}</span>
            <span className={`${local.muted} ${local.cell}`}>{game.package}</span>
            <span className={`${local.latest} ${local.cell}`}>{game.latest ? `up to ${game.latest}` : "no release"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
